import { useNavigate } from 'react-router-dom';
import { Button, Input, InputWrapperAuth } from '@/components/ui';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRightCircle, Lock, Mail } from 'lucide-react';
import { useLoginAdmin } from '@/services/mutations/admin';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';

const schema = z.object({
	email: z.string().max(255, 'Email inválido').email('Email inválido'),
	password: z.string().min(1, 'A senha é obrigatória'),
});

type FormData = z.infer<typeof schema>;

const AdminLogin = () => {
	const navigate = useNavigate();
	const { mutateAsync } = useLoginAdmin();

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	const iconCommonClass = 'w-4 h-4 text-stone-400';

	const onSubmit = async (data: FormData) => {
		const res = await mutateAsync({
			email: data.email,
			password: data.password,
		});
		if (res?.success) navigate('/admin/dashboard');
	};

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Admin Login | Plumaa</title>
			</Helmet>

			<Header />

			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[440px] flex-col gap-6 rounded-[2rem] bg-white p-6 shadow-sm md:p-10">
					<div className="space-y-1 text-center">
						<h1 className="text-2xl font-black tracking-tight text-stone-900 md:text-3xl">
							Admin Login
						</h1>
						<p className="text-xs font-medium text-stone-400 md:text-sm">
							Acesso restrito ao Painel Administrativo
						</p>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-5"
					>
						<div className="space-y-4">
							<div className="space-y-1.5">
								<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
									E-MAIL
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Mail className={iconCommonClass} />}
									error={errors.email?.message}
									value={watch('email')}
									displayError
								>
									<Input
										{...register('email')}
										placeholder="admin@exemplo.com"
										className="h-10 border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1.5">
								<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
									SENHA
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errors.password?.message}
									value={watch('password')}
									displayError
								>
									<Input
										type="password"
										{...register('password')}
										placeholder="********"
										className="h-10 border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>
						</div>

						<Button
							type="submit"
							className="w-full justify-center rounded-xl border-none bg-[#1a1a1a] py-4 text-sm font-bold text-white transition-all hover:bg-stone-800"
							text="Entrar"
							endIcon={
								<ArrowRightCircle size={16} className="ml-1" />
							}
						/>
					</form>
				</div>
			</main>
		</div>
	);
};

export { AdminLogin };
